// src/app/shell/SectionNavMobile.tsx
// Shared mobile section-switcher dropdown for full-canvas BareLayout pages
// (Settings, Council, Owner, More, Creators-Hub). Phase 5 — spec §5.
import { useState } from 'react';
import styled from 'styled-components';
import { Popover } from '@/ui/Popover';
import { Icon } from '@/icons';
import type { IconName } from '@/icons';
import { media } from '@/theme/media';

export interface SectionNavMobileItem {
  id: string;
  label: string;
  icon?: IconName;
}

export interface SectionNavMobileProps {
  sections: SectionNavMobileItem[];
  activeId: string;
  onSelect: (id: string) => void;
}

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  max-width: 260px;
  transition: background 120ms ease;
  &:hover {
    background: ${({ theme }) => theme.color.glassBg};
    opacity: 0.85;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
  /* Only render on mobile. Desktop/tablet callers gate via useViewport, but this adds a CSS safety net */
  ${media.tabletUp`display: none;`}
`;

const Label = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Chevron = styled(Icon)`
  flex-shrink: 0;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  min-width: 220px;
  max-height: 60vh;
  overflow-y: auto;
`;

const Item = styled.li``;

const Btn = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: ${({ theme, $active }) => ($active ? theme.color.glassBorder : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.color.textPrimary : theme.color.textMuted)};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  text-align: left;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.sm};
  &:hover {
    background: ${({ theme }) => theme.color.hover};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

export function SectionNavMobile({ sections, activeId, onSelect }: SectionNavMobileProps) {
  const activeSection = sections.find((s) => s.id === activeId) ?? sections[0];
  const [open, setOpen] = useState(false);

  function handleSelect(id: string) {
    onSelect(id);
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Trigger
          aria-label={`Current section: ${activeSection?.label ?? ''}. Tap to switch sections.`}
          data-testid="section-nav-mobile-trigger"
        >
          {activeSection?.icon && <Icon name={activeSection.icon} size={16} />}
          <Label>{activeSection?.label ?? 'Section'}</Label>
          <Chevron name="chevron-down" size={14} />
        </Trigger>
      }
    >
      <List
        role="menu"
        aria-label="Select section"
        data-testid="section-nav-mobile-list"
      >
        {sections.map((s) => (
          <Item key={s.id} role="none">
            <Btn
              role="menuitem"
              $active={s.id === activeId}
              aria-current={s.id === activeId ? 'page' : undefined}
              onClick={() => handleSelect(s.id)}
              data-testid={`section-nav-mobile-item-${s.id}`}
            >
              {s.icon && <Icon name={s.icon} size={16} />}
              {s.label}
            </Btn>
          </Item>
        ))}
      </List>
    </Popover>
  );
}
